import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { reviewMagazzinoItems } from "@/components/MagazzinoReview";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

// Dopo il salvataggio di una fattura riepilogativa: crea/collega i DDT citati, con risoluzione doppioni.
export const DdtRiferitiDialog = ({ fattura, items, onClose, onDone }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = !!fattura && (items || []).length > 0;

  useEffect(() => {
    if (!open) { setRows(null); return; }
    api.post(`/fatture/${fattura.id}/ddt-riferiti/check`, { items })
      .then(({ data }) => setRows(data.items.map((r) => ({
        ...r, action: r.matches.length ? "link" : "add", target_id: r.matches[0]?.id || null,
      }))))
      .catch(() => { toast.error("Errore controllo DDT"); onClose(); });
    /* eslint-disable-next-line */
  }, [open]);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const apply = async () => {
    setBusy(true);
    try {
      const flat = [];
      rows.forEach((r, i) => { if (r.action === "add") (r.articoli || []).forEach((a) => flat.push({ ...a, _row: i })); });
      let reviewed = flat;
      if (flat.length) {
        reviewed = await reviewMagazzinoItems(flat.map(({ _row, ...a }) => a));
        if (reviewed === null) return;
      }
      const byRow = {};
      reviewed.forEach((a, k) => { (byRow[flat[k]._row] ||= []).push(a); });
      const { data } = await api.post(`/fatture/${fattura.id}/ddt-riferiti/apply`, {
        items: rows.map(({ numero, data: d, importo, action, target_id, articoli }, i) => ({
          numero, data: d, importo, action, target_id, articoli: action === "add" ? (byRow[i] || []) : (articoli || []),
        })),
      });
      const MAG = { gia_caricato_dai_ddt: " · Magazzino già caricato dai DDT", caricato_da_fattura: " · Articoli fuori DDT caricati dalla fattura" };
      const car = data.carichi_ddt ? ` · Magazzino caricato da ${data.carichi_ddt} DDT` : "";
      toast.success(`DDT: ${data.creati} creati · ${data.collegati} collegati · ${data.sovrascritti} sovrascritti · ${data.saltati} saltati${car}${MAG[data.magazzino] || ""}`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore collegamento DDT"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ddt-riferiti-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Truck className="h-4 w-4 text-primary" /> DDT citati nella fattura n. {fattura?.numero}</DialogTitle>
          <DialogDescription>Controlla i DDT trovati: quelli nuovi vengono creati, quelli già presenti collegati.</DialogDescription>
        </DialogHeader>
        {!rows ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" /> : (
          <div className="divide-y border rounded-md">
            {rows.map((r, i) => <RigaDdt key={i} r={r} i={i} setRow={setRow} />)}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} data-testid="ddt-riferiti-cancel">Più tardi</Button>
          <Button onClick={apply} disabled={!rows || busy} data-testid="ddt-riferiti-confirm">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Conferma
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const RigaDdt = ({ r, i, setRow }) => (
  <div className="p-3 text-sm space-y-2" data-testid={`ddt-riferito-${i}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div><b>DDT n. {r.numero || "s.n."}</b> · {r.data || "—"} {r.importo ? `· ${fmt(r.importo)}` : ""}</div>
      <Select value={r.action} onValueChange={(v) => setRow(i, { action: v })}>
        <SelectTrigger className="w-52 h-8 text-xs" data-testid={`ddt-riferito-action-${i}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {r.matches.length > 0 && <SelectItem value="link">Collega quello esistente</SelectItem>}
          {r.matches.length > 0 && <SelectItem value="overwrite">Sovrascrivi</SelectItem>}
          <SelectItem value="add">{r.matches.length ? "Aggiungi lo stesso" : "Crea nuovo DDT"}</SelectItem>
          <SelectItem value="skip">Salta</SelectItem>
        </SelectContent>
      </Select>
    </div>
    {(r.articoli || []).length > 0 && (
      <details className="text-xs" data-testid={`ddt-riferito-articoli-${i}`}>
        <summary className="cursor-pointer text-primary">{r.articoli.length} articoli</summary>
        <ul className="mt-1 pl-4 list-disc text-muted-foreground">
          {r.articoli.map((a, k) => <li key={k}>{a.nome} · {a.quantita} {a.unita_misura} {a.codice ? `· cod. ${a.codice}` : ""}</li>)}
        </ul>
      </details>
    )}
    {r.matches.length > 0 && (
      <div className="space-y-1">
        <div className="text-[11px] text-amber-700">Già presente:</div>
        {r.matches.map((m) => (
          <label key={m.id} className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="radio" checked={r.target_id === m.id} onChange={() => setRow(i, { target_id: m.id })} data-testid={`ddt-riferito-match-${m.id}`} />
            n. {m.numero || "s.n."} · {m.data} · {fmt(m.importo_stimato)} {m.fattura_id ? "· già fatturato" : ""}
          </label>
        ))}
      </div>
    )}
  </div>
);
